import { Form, Row, Col, Button, Card } from 'react-bootstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { useState, useEffect } from 'react';

const SalaryStructureForm = ({ data, onChange, errors, formData }) => {
  const [calculations, setCalculations] = useState({
    grossSalary: 0,
    totalDeductions: 0,
    netSalary: 0,
    ctc: 0,
    employerContribution: 0
  });

  useEffect(() => {
    calculateSalary();
  }, [data]);

  const handleChange = (field, value) => {
    onChange('salary', field, parseFloat(value) || 0);
  };

  const handleOtherAllowanceChange = (index, field, value) => {
    const newAllowances = [...(data.otherAllowances || [])];
    newAllowances[index] = {
      ...newAllowances[index],
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    };
    onChange('salary', 'otherAllowances', newAllowances);
  };

  const addOtherAllowance = () => {
    const newAllowances = [...(data.otherAllowances || []), { name: '', amount: 0 }];
    onChange('salary', 'otherAllowances', newAllowances);
  };

  const removeOtherAllowance = (index) => {
    const newAllowances = data.otherAllowances.filter((_, i) => i !== index);
    onChange('salary', 'otherAllowances', newAllowances);
  };

  const calculateSalary = () => {
    const basicSalary = parseFloat(data.basicSalary) || 0;
    const hra = parseFloat(data.hra) || 0;
    const da = parseFloat(data.da) || 0;
    const conveyance = parseFloat(data.conveyance) || 0;
    const medicalAllowance = parseFloat(data.medicalAllowance) || 0;

    const otherAllowancesTotal = (data.otherAllowances || []).reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );

    const grossSalary = basicSalary + hra + da + conveyance + medicalAllowance + otherAllowancesTotal;

    const pf = parseFloat(data.pf) || 0;
    const esi = parseFloat(data.esi) || 0;
    const professionalTax = parseFloat(data.professionalTax) || 0;

    const totalDeductions = pf + esi + professionalTax;
    const netSalary = grossSalary - totalDeductions;

    // Employer Contributions
    // Employer PF is typically 12% of basic. Only calculate if employee PF is being deducted.
    const employerPF = pf > 0 ? (basicSalary * 0.12) : 0;
    // Employer ESI is typically 3.25% of gross. Only calculate if employee ESI is being deducted.
    const employerESI = esi > 0 ? (grossSalary * 0.0325) : 0;

    const employerContribution = employerPF + employerESI;
    const ctc = grossSalary + employerContribution;

    setCalculations({
      grossSalary,
      totalDeductions,
      netSalary,
      ctc,
      employerContribution
    });
  };

  return (
    <div>
      <h5 className="mb-3">Salary Structure</h5>
      
      <Card className="mb-3 bg-light">
        <Card.Body>
          <h6 className="mb-3">Earnings</h6>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Basic Salary</Form.Label>
                <Form.Control
                  type="number"
                  value={data.basicSalary}
                  onChange={(e) => handleChange('basicSalary', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>HRA (House Rent Allowance)</Form.Label>
                <Form.Control
                  type="number"
                  value={data.hra}
                  onChange={(e) => handleChange('hra', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>DA (Dearness Allowance)</Form.Label>
                <Form.Control
                  type="number"
                  value={data.da}
                  onChange={(e) => handleChange('da', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Conveyance Allowance</Form.Label>
                <Form.Control
                  type="number"
                  value={data.conveyance}
                  onChange={(e) => handleChange('conveyance', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Medical Allowance</Form.Label>
                <Form.Control
                  type="number"
                  value={data.medicalAllowance}
                  onChange={(e) => handleChange('medicalAllowance', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mb-3 mt-4">Other Allowances</h6>
          {(data.otherAllowances || []).map((allowance, index) => (
            <Row key={index} className="mb-2">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Allowance name"
                  value={allowance.name}
                  onChange={(e) => handleOtherAllowanceChange(index, 'name', e.target.value)}
                />
              </Col>
              <Col md={5}>
                <Form.Control
                  type="number"
                  placeholder="Amount"
                  value={allowance.amount}
                  onChange={(e) => handleOtherAllowanceChange(index, 'amount', e.target.value)}
                  min="0"
                />
              </Col>
              <Col md={2}>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => removeOtherAllowance(index)}
                >
                  <FaTrash />
                </Button>
              </Col>
            </Row>
          ))}
          
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={addOtherAllowance}
            className="mt-2"
          >
            <FaPlus /> Add Other Allowance
          </Button>

          <div className="mt-4 p-3 bg-white rounded">
            <h6 className="text-success">
              Gross Salary: ₹{calculations.grossSalary.toLocaleString('en-IN')}
            </h6>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3 bg-light">
        <Card.Body>
          <h6 className="mb-3">Deductions</h6>
          
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>PF (Provident Fund)</Form.Label>
                <Form.Control
                  type="number"
                  value={data.pf}
                  onChange={(e) => handleChange('pf', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>ESI (Employee State Insurance)</Form.Label>
                <Form.Control
                  type="number"
                  value={data.esi}
                  onChange={(e) => handleChange('esi', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Professional Tax</Form.Label>
                <Form.Control
                  type="number"
                  value={data.professionalTax}
                  onChange={(e) => handleChange('professionalTax', e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-3 p-3 bg-white rounded">
            <h6 className="text-danger">
              Total Deductions: ₹{calculations.totalDeductions.toLocaleString('en-IN')}
            </h6>
          </div>
        </Card.Body>
      </Card>

      <Card className="bg-primary text-white">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={5}>
              <h6 className="opacity-75 mb-1">Net Salary (Take Home)</h6>
              <h3 className="mb-0">₹{calculations.netSalary.toLocaleString('en-IN')}</h3>
            </Col>
            <Col md={2} className="text-center">
              <div className="border-end border-light opacity-50" style={{ height: '40px' }}></div>
            </Col>
            <Col md={5} className="text-end">
              <h6 className="opacity-75 mb-1">CTC (Cost to Company)</h6>
              <h3 className="mb-0">₹{calculations.ctc.toLocaleString('en-IN')}</h3>
              {calculations.employerContribution > 0 && (
                <small className="d-block text-light opacity-75 mt-1" style={{ fontSize: '0.75rem' }}>
                  (Includes ₹{calculations.employerContribution.toLocaleString('en-IN')} Employer Contribution)
                </small>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SalaryStructureForm;
